/* eslint-disable @nx/enforce-module-boundaries */
import { useQuery } from '@apollo/client';
import {
  Center,
  HStack,
  IconButton,
  Link,
  Text,
  VStack,
} from '@chakra-ui/react';
import ArrowRightAltSharpIcon from '@gamma/ui/icons/ArrowRightAltSharpIcon';
import ChevronLeftSharpIcon from '@gamma/ui/icons/ChevronLeftSharpIcon';
import ChevronRightSharpIcon from '@gamma/ui/icons/ChevronRightSharpIcon';
import DataAlertSharpIcon from '@gamma/ui/icons/DataAlertSharpIcon';
import { FlushPanelContent, Panel, PanelHeader } from '@gamma/ui/layout';
import { GraphQLReqStatus } from '@gamma/ui/progress';
import { AnomalyAlert } from '../../../../queries';
import { Alert } from '@investigator/shared/generated/graphql';
import { SanitizedAnomalyAlert } from '../RelatedDetections/Components/RelatedDetectionsAlerts/Components/DetectionAlerts/DetectionAlerts';
import { QUERY_ALERTS_ANOMALY_PAGINATED } from '../../../../queries';
import camelcase from 'lodash/camelCase';
import moment from 'moment';
import { useOutletContext } from 'react-router';
import { IDetectionContext } from '../../..';
import { QueryAlertsAnomalyPaginatedQuery } from '@investigator/shared/generated/graphql';
import { i18n } from '@investigator/shared/localization';
import { SlideDrawerContext } from '@investigator/shared/providers';
import { DetailsGridRow } from '../../../../../detections/components';
import { FC, useCallback, useContext, useMemo, useState } from 'react';
import { AuthContext } from '../../../../../../shared/providers';
import { AlertRowSplitAnomaly } from '../RelatedDetections/Components/RelatedDetectionsAlerts/Components/DetectionAlerts/Components/Anomaly';
import { AlertRowSplitBaseline } from '../RelatedDetections/Components/RelatedDetectionsAlerts/Components/DetectionAlerts/Components/Baseline';
import {
  SeverityBucketLookup,
  SeverityColorMap,
} from '@investigator/shared/utils';

type AnomalyType =
  | 'anomalousServiceOnInternetFacingServer'
  | 'anomalousSshClientOnSubnet'
  | 'anomalousHttpUserAgentFamilyIdentifiedOnSubnet'
  | 'anomalousServiceIdentifiedBetweenInternalSubnets'
  | 'anomalousSshDestinationFromInternalSubnet';

// TODO: move this to a common place
export function formatNamedString(
  template: string,
  values: Record<string, string | number>,
): string {
  return template.replace(/\{([^}]+)\}/g, (_, key) => {
    return values[key] != null ? String(values[key]) : `{${key}}`;
  });
}

// Helper type for extra fields sometimes present on anomalyAlert
interface AnomalyAlertWithBaseline extends AnomalyAlert {
  destination_ip?: string;
  baselining_start_timestamp?: number;
  baselining_end_timestamp?: number;
  alert_timestamp?: { observed?: number };
}

export const AnomalyPanel: FC = () => {
  const { detectionData } = useOutletContext<IDetectionContext>() || {};

  const detection = detectionData?.getDetections?.detections?.[0];
  const alertInfo = detection?.alert_info;
  const severity = detection?.rank?.severity ?? 0;

  const { userPrivileges } = useContext(AuthContext);
  const tenant = detection?.tenant_info?.tenant_id;
  const isGPTPrivateDataEnabled = Boolean(
    tenant ? userPrivileges?.[tenant]?.chatgpt_private_data : false,
  );

  const anomalyType = useMemo(
    () => camelcase(alertInfo?.alert_name ?? undefined),
    [alertInfo?.alert_name],
  ) as AnomalyType;

  const {
    detectionSummary: { anomalyPanel, anomalyCopy },
  } = i18n.pages.detections;

  const anomalyTranslations = anomalyCopy[anomalyType];
  const scoreColor = SeverityColorMap[SeverityBucketLookup[severity]];

  const [offset, setOffset] = useState(0);

  const {
    data: alertData,
    loading: alertLoading,
    refetch,
  } = useQuery<QueryAlertsAnomalyPaginatedQuery>(
    QUERY_ALERTS_ANOMALY_PAGINATED,
    {
      variables: {
        query: `{
        "query": {
          "bool": {
            "must": [
              {"term": {"alert_info.content_id": "${alertInfo?.content_id}"}},
              {"term": {"alert_entity.entity_id": "${detection?.alert_entity?.entity_id}"}},
              {"range": {"alert_timestamp.start": {"lte": ${detection?.latest_start_timestamp}}}},
              {"range": {"alert_timestamp.end": {"gte": ${detection?.earliest_start_timestamp}}}}
            ]
          }
        }
      }`,
        size: 1,
        offset,
        sort: [
          {
            sort_by: 'alert_timestamp.start',
            sort_dir: 'desc',
          },
        ],
      },
      skip:
        !alertInfo?.content_id ||
        !detection?.alert_entity?.entity_id ||
        !detection?.earliest_start_timestamp ||
        !detection?.latest_start_timestamp,
      notifyOnNetworkStatusChange: true,
    },
  );

  const [alert] = alertData?.queryAlertsPaginated?.alerts ?? [];

  const anomalyAlert = alert as AnomalyAlert;

  const total = alertData?.queryAlertsPaginated?.total_items ?? 0;
  const hasNext = offset + 1 < total;

  const anomalyAlertWithBaseline = anomalyAlert as AnomalyAlertWithBaseline;

  const replacementValues = useMemo(
    () => ({
      host: anomalyAlertWithBaseline?.entity ?? 'HOST_NOT_FOUND',
      sourceIp: anomalyAlertWithBaseline?.source_ip ?? 'SOURCE_IP_NOT_FOUND',
      destinationIp:
        anomalyAlertWithBaseline?.destination_ip ?? 'DESTINATION_IP_NOT_FOUND',
      anomalousItem:
        anomalyAlertWithBaseline?.item ?? 'ANOMALOUS_ITEM_NOT_FOUND',
      entityTrainingItems:
        anomalyAlertWithBaseline?.entity_training_items?.join(', ') ??
        'BASELINE_ITEMS_NOT_FOUND',
    }),
    [
      anomalyAlertWithBaseline?.entity,
      anomalyAlertWithBaseline?.source_ip,
      anomalyAlertWithBaseline?.destination_ip,
      anomalyAlertWithBaseline?.item,
      anomalyAlertWithBaseline?.entity_training_items,
    ],
  );

  const {
    setPortalType,
    setIsSlideDrawerOpen,
    setActiveAlert,
    setSlideDrawerBody,
    setSlideDrawerTitle,
    setSlideDrawerActions,
  } = useContext(SlideDrawerContext);

  const handleAlertPortal = useCallback(() => {
    setPortalType?.('alert');
    setSlideDrawerTitle?.(undefined);
    const alertContent = anomalyAlert ? (
      <AlertRowSplitAnomaly
        item={anomalyAlert as unknown as SanitizedAnomalyAlert}
        loading={alertLoading}
        isGPTPrivateDataEnabled={isGPTPrivateDataEnabled}
        logScaleQuery={''}
      />
    ) : null;

    setSlideDrawerBody?.(alertContent);
    setSlideDrawerActions?.(null);
    setIsSlideDrawerOpen?.(true);
    setActiveAlert?.(anomalyAlert);
  }, [
    anomalyAlert,
    alertLoading,
    isGPTPrivateDataEnabled,
    setActiveAlert,
    setIsSlideDrawerOpen,
    setPortalType,
    setSlideDrawerBody,
    setSlideDrawerTitle,
    setSlideDrawerActions,
  ]);

  const handleBaselinePortal = useCallback(() => {
    setPortalType?.('baseline');

    const baselineTitle = (() => {
      const dateRange =
        anomalyAlertWithBaseline?.baselining_start_timestamp &&
        anomalyAlertWithBaseline?.baselining_end_timestamp
          ? ` (${moment.unix(anomalyAlertWithBaseline.baselining_start_timestamp).format('MMM D, YYYY')} - ${moment.unix(anomalyAlertWithBaseline.baselining_end_timestamp).format('MMM D, YYYY')})`
          : '';
      return `Baseline Data${dateRange}`;
    })();

    setSlideDrawerTitle?.(baselineTitle);
    setSlideDrawerBody?.(
      <AlertRowSplitBaseline item={anomalyAlertWithBaseline as Alert} />,
    );
    setSlideDrawerActions?.(null);
    setIsSlideDrawerOpen?.(true);
    setActiveAlert?.(anomalyAlertWithBaseline);
  }, [
    anomalyAlertWithBaseline,
    setActiveAlert,
    setIsSlideDrawerOpen,
    setPortalType,
    setSlideDrawerBody,
    setSlideDrawerTitle,
    setSlideDrawerActions,
  ]);

  if (alertLoading) {
    return (
      <Center w="100%">
        <GraphQLReqStatus loading />
      </Center>
    );
  }

  return (
    <Panel
      w="100%"
      color="text.primary"
      bgColor={`${scoreColor}.500`}
      borderColor={`${scoreColor}.500`}
      data-testid="anomaly-panel"
      borderRadius={4}
    >
      <PanelHeader>
        <HStack>
          <DataAlertSharpIcon />
          <Text
            fontWeight={600}
            fontSize="14px"
            lineHeight="20px"
            letterSpacing="0.16px"
          >
            {anomalyPanel.title}
          </Text>
        </HStack>
      </PanelHeader>
      <FlushPanelContent
        border="1px transparent solid"
        borderRadius={4}
        padding="2px"
      >
        <VStack bgColor="background" borderRadius={4} padding={4}>
          <HStack w="100%" justifyContent="start" mb={2}>
            <IconButton
              color="text.primary"
              aria-label="Previous alert"
              icon={<ChevronLeftSharpIcon />}
              bgColor="background"
              isDisabled={offset === 0}
              onClick={() => {
                if (offset > 0) {
                  setIsSlideDrawerOpen?.(false);
                  setActiveAlert?.(null);
                  setSlideDrawerActions?.(null);
                  const prevOffset = offset - 1;
                  setOffset(prevOffset);
                  refetch({ offset: prevOffset });
                }
              }}
              variant="ghost"
              _hover={{ bg: 'gray.700', color: 'text.primary.100' }}
              _active={{ bg: 'gray.600' }}
              _disabled={{
                color: 'text.primary',
                bg: 'transparent',
                cursor: 'not-allowed',
                _hover: { bg: 'transparent' },
                _active: { bg: 'transparent' },
              }}
            />
            <Link
              color="text.link"
              onClick={handleAlertPortal}
            >{`${anomalyPanel.alert} ${offset + 1}`}</Link>
            <IconButton
              color="text.primary"
              aria-label="Next alert"
              bgColor="background"
              icon={<ChevronRightSharpIcon />}
              isDisabled={!hasNext}
              onClick={() => {
                if (hasNext) {
                  setIsSlideDrawerOpen?.(false);
                  setActiveAlert?.(null);
                  setSlideDrawerActions?.(null);
                  const nextOffset = offset + 1;
                  setOffset(nextOffset);
                  refetch({ offset: nextOffset });
                }
              }}
              variant="ghost"
              _hover={{ bg: 'gray.700', color: 'text.primary.100' }}
              _active={{ bg: 'gray.600' }}
              _disabled={{
                color: 'text.primary',
                bg: 'transparent',
                cursor: 'not-allowed',
                _hover: { bg: 'transparent' },
                _active: { bg: 'transparent' },
              }}
            />
          </HStack>
          <DetailsGridRow
            isDetailsPage
            title={`${anomalyPanel.anomalousBehaviorLabel}${
              anomalyAlertWithBaseline?.alert_timestamp?.observed
                ? ` (${moment.unix(anomalyAlertWithBaseline.alert_timestamp?.observed ?? 0).format('MMM D, YYYY')})`
                : ''
            }`}
            data-testid="detection-content-anomaly-panel-anomalous-behavior"
          >
            <Text
              fontSize="14px"
              fontWeight={400}
              lineHeight="20px"
              color="text.primary"
            >
              {formatNamedString(
                anomalyTranslations?.anomalousBehavior ?? '',
                replacementValues,
              )}
            </Text>
          </DetailsGridRow>
          <DetailsGridRow
            isDetailsPage
            title={`${anomalyPanel.normalBehaviorLabel}${
              anomalyAlertWithBaseline?.baselining_start_timestamp &&
              anomalyAlertWithBaseline?.baselining_end_timestamp
                ? ` (${moment.unix(anomalyAlertWithBaseline.baselining_start_timestamp ?? 0).format('MMM D, YYYY')} - ${moment.unix(anomalyAlertWithBaseline.baselining_end_timestamp ?? 0).format('MMM D, YYYY')})`
                : ''
            }`}
            data-testid="detection-content-doc-next-steps"
          >
            <Text
              fontSize="14px"
              fontWeight={400}
              lineHeight="20px"
              color="text.primary"
            >
              {formatNamedString(
                anomalyTranslations?.normalBehavior ?? '',
                replacementValues,
              )}
            </Text>
          </DetailsGridRow>
          <HStack alignItems="start" w="100%">
            <Link
              onClick={handleBaselinePortal}
              color="text.link"
              _hover={{ textDecoration: 'none' }}
            >
              <HStack alignItems="center" w="100%">
                <Text>View Baseline Data</Text>
                <ArrowRightAltSharpIcon />
              </HStack>
            </Link>
          </HStack>
        </VStack>
      </FlushPanelContent>
    </Panel>
  );
};
